import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase/config';
import { COLLECTIONS, splitPath } from '@elka/firebase';
import type { CostEstimate, Quote, Deal, DealQuotingStatus } from '@elka/types';

/** Outcome of a deal quoting-status sync, for callers that react to it (e.g. the won-deal prompt). */
export interface DealQuotingSyncResult {
  /** Set when the deal is already linked to an elka-manager project. */
  projectId?: string;
  /** True only when THIS call wrote stage:'won' (not when the deal was already won). */
  advancedToWon: boolean;
}

/**
 * Updates the quoting status fields on a CRM deal document.
 *
 * Queries the latest cost estimate and quote for the given dealId,
 * derives the quotingStatus using 4-stage logic,
 * and writes the status + related IDs back to the deal document.
 *
 * This is called after any estimate or quote mutation to keep the deal doc in sync.
 * Fire-and-forget: it never throws — failures are logged and reported as advancedToWon:false.
 */
export async function updateDealQuotingStatus(dealId: string): Promise<DealQuotingSyncResult> {
  if (!dealId) {
    console.warn('[updateDealQuotingStatus] No dealId provided');
    return { advancedToWon: false };
  }

  try {
    let quotingStatus: DealQuotingStatus = 'draft';

    // Query latest cost estimate for this deal
    const estimatesQuery = query(
      collection(db, ...splitPath(COLLECTIONS.quotes.costEstimates)),
      where('dealId', '==', dealId),
      orderBy('createdAt', 'desc')
    );
    const estimatesSnap = await getDocs(estimatesQuery);
    const costEstimate = estimatesSnap.docs[0]
      ? ({ id: estimatesSnap.docs[0].id, ...estimatesSnap.docs[0].data() } as CostEstimate)
      : null;

    // Query latest quote for this deal
    const quotesQuery = query(
      collection(db, ...splitPath(COLLECTIONS.quotes.quotes)),
      where('dealId', '==', dealId),
      orderBy('createdAt', 'desc')
    );
    const quotesSnap = await getDocs(quotesQuery);
    const quote = quotesSnap.docs[0]
      ? ({ id: quotesSnap.docs[0].id, ...quotesSnap.docs[0].data() } as Quote)
      : null;

    // Check if there are bid requests
    const bidRequestsQuery = query(
      collection(db, ...splitPath(COLLECTIONS.quotes.bidRequests)),
      where('dealId', '==', dealId)
    );
    const bidRequestsSnap = await getDocs(bidRequestsQuery);
    const hasBidRequests = !bidRequestsSnap.empty;

    // Derive quotingStatus
    if (quote?.status === 'accepted') {
      quotingStatus = 'accepted';
    } else if (quote?.status === 'sent' || quote?.status === 'finalized') {
      quotingStatus = 'quoted';
    } else if (quote) {
      quotingStatus = 'quoting';
    } else if (costEstimate) {
      const priceable = costEstimate.items.filter((i) => !i.isHeader);
      const priced = priceable.filter((i) => !!i.supplierPrice || !!i.priceEntries?.length);
      const pricingPercent =
        priceable.length > 0 ? Math.round((priced.length / priceable.length) * 100) : 0;
      const hasTargetPrice = !!(costEstimate.targetPrice && costEstimate.targetPrice > 0);

      if (pricingPercent >= 90 && hasTargetPrice) {
        quotingStatus = 'ready-to-quote';
      } else if (hasBidRequests || costEstimate.status === 'bid-sent') {
        quotingStatus = 'supplier-pricing';
      } else if (costEstimate.items.filter((i) => !i.isHeader).length > 0) {
        quotingStatus = 'estimating';
      } else {
        quotingStatus = 'boq-intake';
      }
    }

    // Write back to the deal document
    const dealRef = doc(db, ...splitPath(COLLECTIONS.crm.deals), dealId);

    const dealUpdatePayload: Record<string, unknown> = {
      quotingStatus,
      costEstimateId: costEstimate?.id || null,
      quoteId: quote?.id || null,
      quoteTotal: quote?.total || null,
      quoteSubtotal: quote?.subtotal || null,
      updatedAt: serverTimestamp(),
    };

    // Auto-advance deal stage to 'won' when the quote is accepted, but only if the
    // stage hasn't already been curated to 'won' or 'lost' by the sales team.
    // Only this branch reads the deal — the other paths deliberately stay read-free,
    // so projectId is reported only when we already had the doc in hand.
    let projectId: string | undefined;
    let advancedToWon = false;
    if (quotingStatus === 'accepted') {
      const dealSnap = await getDoc(dealRef);
      const deal = dealSnap.data() as Pick<Deal, 'stage' | 'projectId'> | undefined;
      const currentStage: string | undefined = deal?.stage;
      projectId = deal?.projectId;
      if (currentStage && !['won', 'lost'].includes(currentStage)) {
        dealUpdatePayload.stage = 'won';
        advancedToWon = true;
      }
    }

    await updateDoc(dealRef, dealUpdatePayload);

    if (import.meta.env.DEV) {
      console.debug(`[updateDealQuotingStatus] Updated deal ${dealId}:`, {
        quotingStatus,
        costEstimateId: costEstimate?.id,
        quoteId: quote?.id,
      });
    }

    return { projectId, advancedToWon };
  } catch (error) {
    console.error('[updateDealQuotingStatus] Failed to update deal status:', error);
    // Don't throw - this is a fire-and-forget helper
    return { advancedToWon: false };
  }
}
